import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, CheckCheck, MessageSquare, ThumbsUp, Info } from 'lucide-react';
import { toast } from 'sonner';
import { getApiExtra, getApiList, getApiErrorMessage } from '../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
        params: { standard: true }
      });
      setNotifications(getApiList(response));
      setUnreadCount(getApiExtra(response)?.unread_count || 0);
    } catch (err) {
      const errorMsg = getApiErrorMessage(err, 'Failed to fetch notifications');
      setNotifications([]);
      setUnreadCount(0);
      // Only show toast on timeout or network errors, not on auth failures
      if (err.code !== 'ERR_BAD_REQUEST' && err.response?.status !== 401) {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      // Silently fail - will refresh on next fetch
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      // Silently fail - will refresh on next fetch
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.claim_id) {
      navigate(`/posts/${notification.claim_id}`);
    }
  };

  const getNotificationIcon = (type, annotationType) => {
    if (type === 'annotation') {
      if (annotationType === 'support') {
        return <ThumbsUp size={18} className="text-green-500" />;
      } else if (annotationType === 'contradict') {
        return <MessageSquare size={18} className="text-red-500" />;
      } else {
        return <Info size={18} className="text-blue-500" />;
      }
    }
    return <Bell size={18} className="text-muted-foreground" />;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading notifications...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="playfair text-2xl md:text-3xl font-bold">Notifications</h1>
            {unreadCount > 0 && (
              <p className="text-sm text-muted-foreground mt-1">
                {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              data-testid="mark-all-read-btn"
              className="flex items-center gap-2 px-4 py-2 text-sm text-primary hover:bg-secondary rounded-sm transition-colors"
            >
              <CheckCheck size={16} />
              Mark all as read
            </button>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="text-center py-16">
            <Bell size={48} className="mx-auto text-muted-foreground/50 mb-4" />
            <h2 className="text-lg font-medium mb-2">No notifications yet</h2>
            <p className="text-muted-foreground text-sm">
              When someone supports, contradicts, or adds context to your posts, you'll see it here.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map((notification) => (
              <button
                key={notification.id}
                data-testid={`notification-${notification.id}`}
                onClick={() => handleNotificationClick(notification)}
                className={`w-full text-left p-4 rounded-sm border transition-colors ${
                  notification.read 
                    ? 'bg-card border-border hover:bg-secondary/50' 
                    : 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-950/30'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {getNotificationIcon(notification.type, notification.annotation_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${notification.read ? '' : 'font-medium'}`}>
                      {notification.message}
                    </p>
                    {notification.claim_preview && (
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        "{notification.claim_preview}"
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      {formatTime(notification.created_at)}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
